'use client';
import {type ComponentPropsWithoutRef, useRef} from 'react';
import NextImage from 'next/image';

import type {Image} from '@/modules/foundation/components/image/types';
import {Spinner} from '@/modules/foundation/components/spinner';
import useDimensions from '@/modules/foundation/hooks/useDimensions';
import {cn} from '@/modules/foundation/shared/tailwind';
import type {CommerceProductImageOverlayPlacement} from '@/sanity/schemas/types';
import {assertUnreachable} from '@/shared/assertUnreachable';

import {useDynamicFont} from './font/hooks';

export type OverlayPlacement = {
	centerX: number;
	centerY: number;
	maxWidth: number;
	maxHeight: number;
	rotation: number;
	anchorX: number;
	anchorY: number;
};

/** A dynamic text type that allows rendering text with custom fonts & colors.*/
export type DynamicTextOverlay = {
	_type: 'dynamicTextOverlay';
	font: string;
	fontUrl: string;
	fontSize: number;
	lineHeight: number;
	text: string;
	colorHex: string;
	origin?: 'center' | 'right' | 'left';
	placement: OverlayPlacement;
	icon?: Image;
};

export type ImageOverlay = {
	_type: 'imageOverlay';
	image: Image;
	placement: OverlayPlacement;
};

export type MaskedColorOverlay = {
	_type: 'maskedColorOverlay';
	colorHex: string;
	maskImage: Image;
	placement: OverlayPlacement;
};

export type Overlay = ImageOverlay | DynamicTextOverlay | MaskedColorOverlay;

export type ProductImageStackDescription = {
	image: Image | null;
	altText: string | null;
	overlays: Overlay[];
};

export function getDefaultWatermarkPlacement(): OverlayPlacement {
	return {
		centerX: 0.76,
		centerY: 0.95,
		maxWidth: 0.5,
		maxHeight: 0.2,
		rotation: 0,
		anchorX: 0.5,
		anchorY: 0.5,
	};
}

export function getDefaultOverlayPlacement(): OverlayPlacement {
	return {
		centerX: 0.5,
		centerY: 0.5,
		maxWidth: 1,
		maxHeight: 1,
		rotation: 0,
		anchorX: 0.5,
		anchorY: 0.5,
	};
}

export function getOverlayPlacement(
	placement?: CommerceProductImageOverlayPlacement | null,
): OverlayPlacement {
	const defaultPlacement = getDefaultOverlayPlacement();

	return placement
		? {
				centerX: placement.centerX ?? defaultPlacement.centerX,
				centerY: placement.centerY ?? defaultPlacement.centerY,
				maxWidth: placement.maxWidth ?? defaultPlacement.maxWidth,
				maxHeight: placement.maxHeight ?? defaultPlacement.maxHeight,
				rotation: placement.rotation ?? defaultPlacement.rotation,
				anchorX:
					typeof placement.anchorX !== 'number'
						? defaultPlacement.anchorX
						: placement.anchorX,
				anchorY:
					typeof placement.anchorY !== 'number'
						? defaultPlacement.anchorY
						: placement.anchorY,
			}
		: defaultPlacement;
}

export function getOverlayPlacementAnchors<T extends {anchorX: number; anchorY: number}>({
	anchorX,
	anchorY,
}: T) {
	const objectLeftPercent = anchorX * 100;
	const objectTopPercent = anchorY * 100;

	return {objectLeftPercent, objectTopPercent};
}

const getAbsolutePlacement = (placement: OverlayPlacement) => {
	const width = placement.maxWidth;
	const height = placement.maxHeight;
	const left = -width / 2 + placement.centerX;
	const top = -height / 2 + placement.centerY;

	const widthPercent = width * 100;
	const heightPercent = height * 100;
	const leftPercent = left * 100;
	const topPercent = top * 100;

	return {
		topPercent,
		leftPercent,
		widthPercent,
		heightPercent,
	};
};

type ImageLayerProps = {
	overlay: ImageOverlay;
	sizesPx?: number;
};

export function getOverlayPlacementObjectPosition(
	placement: OverlayPlacement,
	imageAspect: number,
) {
	// Adjust object position to center the image on the anchor point, but clamp to 0-100% to keep
	// the image within the bounds of the placement area:
	const anchorXPercent = placement.anchorX * 100;
	const anchorYPercent = placement.anchorY * 100;

	const placementAspect = placement.maxWidth / placement.maxHeight;

	const imageWidthFactor = imageAspect / placementAspect;
	const imageHeightFactor = placementAspect / imageAspect;

	const widthOffsetFactor = imageWidthFactor / (1 - imageWidthFactor) / 2;
	const heightOffsetFactor = imageHeightFactor / (1 - imageHeightFactor) / 2;

	const widthScale = 1 + widthOffsetFactor + widthOffsetFactor;
	const heightScale = 1 + heightOffsetFactor + heightOffsetFactor;

	const objectPositionLeftPercent = Math.min(
		100,
		Math.max(0, anchorXPercent * widthScale - 100 * widthOffsetFactor),
	);
	const objectPositionTopPercent = Math.min(
		100,
		Math.max(0, anchorYPercent * heightScale - 100 * heightOffsetFactor),
	);

	return {objectPositionLeftPercent, objectPositionTopPercent};
}

function ImageLayer({overlay, sizesPx}: ImageLayerProps) {
	if (!overlay.image) {
		return null;
	}

	const placement = overlay.placement;
	const rotationDeegrees = placement.rotation;

	const {topPercent, leftPercent, widthPercent, heightPercent} = getAbsolutePlacement(placement);

	const imageAspect = overlay.image.width / overlay.image.height;

	const {objectPositionLeftPercent, objectPositionTopPercent} = getOverlayPlacementObjectPosition(
		placement,
		imageAspect,
	);

	return (
		<NextImage
			src={overlay.image.src}
			width={overlay.image.width}
			height={overlay.image.height}
			className="absolute block aspect-square object-contain object-center"
			alt={''}
			sizes={sizesPx ? `${sizesPx * placement.maxWidth}px` : undefined} // SizesPx is the size of the container, we need to scale it down according to the overlay placement
			style={{
				objectFit: 'contain',
				objectPosition: `${objectPositionLeftPercent}% ${objectPositionTopPercent}%`,

				rotate: `${rotationDeegrees}deg`,
				top: topPercent + '%',
				left: leftPercent + '%',
				width: widthPercent + '%',
				height: heightPercent + '%',
			}}
		/>
	);
}

function MaskLayer({overlay}: {overlay: MaskedColorOverlay}) {
	if (!overlay.maskImage) {
		return null;
	}

	const placement = overlay.placement;
	const rotationDeegrees = placement.rotation;

	const {topPercent, leftPercent, widthPercent, heightPercent} = getAbsolutePlacement(placement);

	const imageAspect = overlay.maskImage.width / overlay.maskImage.height;

	const {objectPositionLeftPercent, objectPositionTopPercent} = getOverlayPlacementObjectPosition(
		placement,
		imageAspect,
	);

	return (
		<div
			className="absolute block object-contain object-center"
			style={{
				maskImage: `url(${overlay.maskImage.src})`,
				maskSize: 'contain',
				maskRepeat: 'no-repeat',
				maskPosition: `${objectPositionLeftPercent}% ${objectPositionTopPercent}%`,
				backgroundColor: overlay.colorHex,

				rotate: `${rotationDeegrees}deg`,
				top: topPercent + '%',
				left: leftPercent + '%',
				width: widthPercent + '%',
				height: heightPercent + '%',
			}}
		/>
	);
}

function DynamicTextLayer({overlay}: {overlay: DynamicTextOverlay}) {
	const translateX =
		overlay.origin === 'center' ? '-50%' : overlay.origin === 'right' ? '-100%' : '0%';

	const isLoaded = useDynamicFont({name: overlay.font, url: overlay.fontUrl});

	const placement = overlay.placement;
	const {topPercent, leftPercent, widthPercent, heightPercent} = getAbsolutePlacement(placement);
	const {objectLeftPercent, objectTopPercent} = getOverlayPlacementAnchors(placement);

	if (!isLoaded) {
		return <Spinner title={'loading'} />;
	}

	return (
		<div
			className="@container absolute flex text-nowrap text-white"
			style={{
				fontFamily: overlay.font,
				color: overlay.colorHex,
				top: topPercent + '%',
				left: leftPercent + '%',
				width: widthPercent + '%',
				height: heightPercent + '%',
				rotate: `${placement.rotation}deg`,
			}}
		>
			<div
				className="relative h-full w-full overflow-clip"
				style={{
					fontSize: `${overlay.fontSize}cqi`,
					lineHeight: overlay.lineHeight,
				}}
			>
				<div
					className="absolute flex h-fit w-fit max-w-full flex-row items-center gap-2"
					style={{
						top: objectTopPercent + '%',
						left: objectLeftPercent + '%',
						transform: `translateX(${translateX}) translateY(-50%)`,
					}}
				>
					<div className="flex flex-row items-center gap-2">
						{overlay.icon && (
							<div
								style={{
									width: '1lh',
									height: '1lh',
									objectFit: 'contain',
									objectPosition: 'center',
									maskImage: `url(${overlay.icon.src})`,
									maskSize: 'contain',
									maskRepeat: 'no-repeat',
									maskPosition: 'center',
									backgroundColor: overlay.colorHex,
								}}
							/>
						)}
						{overlay.text}
					</div>
				</div>
			</div>
		</div>
	);
}

type RenderProductImageStackProps = ComponentPropsWithoutRef<'div'> & {
	stack: ProductImageStackDescription;
};

export function ProductImageStack({stack, className, ...rest}: RenderProductImageStackProps) {
	const container = useRef<HTMLDivElement>(null);
	const dimensions = useDimensions(container);

	const sizesPx = dimensions?.width ?? undefined;

	return (
		<div className={cn('relative aspect-square', className)} ref={container} {...rest}>
			{stack.image && (
				<NextImage
					src={stack.image.src}
					width={stack.image.width}
					height={stack.image.height}
					className="absolute top-0 left-0 block h-full w-full object-contain object-center"
					alt={stack.altText ?? ''}
					sizes={sizesPx ? `${sizesPx}px` : undefined}
				/>
			)}
			{stack.overlays.map((overlay, i) => {
				switch (overlay._type) {
					case 'dynamicTextOverlay': {
						return <DynamicTextLayer key={i} overlay={overlay} />;
					}
					case 'imageOverlay': {
						return <ImageLayer key={i} overlay={overlay} sizesPx={sizesPx} />;
					}
					case 'maskedColorOverlay': {
						return <MaskLayer key={i} overlay={overlay} />;
					}
					default:
						assertUnreachable(overlay);
				}
			})}
		</div>
	);
}
